import React from 'react';
import { Box } from '@mui/material';

const toRgba = (hex, alpha) => {
  const clean = (hex || '').replace('#', '');
  const normalized = clean.length === 3
    ? clean.split('').map((char) => char + char).join('')
    : clean;

  if (normalized.length !== 6) return `rgba(255,255,255,${alpha})`;

  const r = parseInt(normalized.substring(0, 2), 16);
  const g = parseInt(normalized.substring(2, 4), 16);
  const b = parseInt(normalized.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const GuidedHintAnimation = ({ variant = 'dashboard', brandColors }) => {
  const primary = brandColors?.primary || '#2A2AC0';
  const secondary = brandColors?.secondary || '#261D56';
  const text = brandColors?.text || '#261D56';
  const glow = toRgba(primary, 0.32);
  const panelFill = toRgba(primary, 0.18);
  const line = toRgba(text, 0.4);

  if (variant === 'chasis') {
    return (
      <Box
        component="svg"
        viewBox="0 0 360 140"
        role="img"
        aria-label="Guia animada para captura de chasis"
        sx={{ width: '100%', maxWidth: 360, height: 'auto', display: 'block', mx: 'auto', my: 1 }}
      >
        <rect x="16" y="20" width="328" height="100" rx="12" fill={panelFill} stroke={line} strokeWidth="2" />
        <rect x="48" y="58" width="264" height="30" rx="6" fill="#fff" opacity="0.95" />
        <text x="180" y="78" textAnchor="middle" fontSize="16" fontFamily="monospace" fill={text}>
          VIN: 8A1BC2D3E4F5G678
        </text>
        <rect x="48" y="58" width="10" height="30" fill={secondary} opacity="0.45">
          <animate attributeName="x" values="48;302;48" dur="2.2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.3;0.9;0.3" dur="2.2s" repeatCount="indefinite" />
        </rect>
        <circle cx="34" cy="34" r="6" fill={primary}>
          <animate attributeName="r" values="4;7;4" dur="1.2s" repeatCount="indefinite" />
        </circle>
      </Box>
    );
  }

  if (variant === 'wheel') {
    return (
      <Box
        component="svg"
        viewBox="0 0 360 140"
        role="img"
        aria-label="Guia animada para rueda de repuesto"
        sx={{ width: '100%', maxWidth: 360, height: 'auto', display: 'block', mx: 'auto', my: 1 }}
      >
        <rect x="20" y="24" width="320" height="92" rx="12" fill={panelFill} stroke={line} strokeWidth="2" />
        <circle cx="112" cy="70" r="34" fill="#111827" stroke={toRgba(primary, 0.7)} strokeWidth="3" />
        <circle cx="112" cy="70" r="17" fill={toRgba(primary, 0.28)} />
        <circle cx="112" cy="70" r="6" fill={primary}>
          <animate attributeName="r" values="5;7;5" dur="1.2s" repeatCount="indefinite" />
        </circle>
        <rect x="172" y="48" width="140" height="8" rx="3" fill={toRgba(text, 0.25)} />
        <rect x="172" y="64" width="126" height="8" rx="3" fill={toRgba(text, 0.25)} />
        <rect x="172" y="80" width="108" height="8" rx="3" fill={toRgba(text, 0.25)} />
        <rect x="80" y="36" width="64" height="68" rx="8" fill="none" stroke={primary} strokeDasharray="6 6" strokeWidth="2">
          <animate attributeName="stroke-dashoffset" values="0;24" dur="1.2s" repeatCount="indefinite" />
        </rect>
      </Box>
    );
  }

  if (variant === 'mechanical') {
    return (
      <Box
        component="svg"
        viewBox="0 0 360 140"
        role="img"
        aria-label="Guia animada para motor"
        sx={{ width: '100%', maxWidth: 360, height: 'auto', display: 'block', mx: 'auto', my: 1 }}
      >
        <rect x="22" y="16" width="316" height="108" rx="14" fill={panelFill} stroke={line} strokeWidth="2" />
        <rect x="48" y="30" width="264" height="80" rx="12" fill="#111827" stroke={toRgba(primary, 0.6)} strokeWidth="2" />

        <rect x="62" y="44" width="92" height="26" rx="6" fill={toRgba(primary, 0.28)} />
        <rect x="162" y="42" width="64" height="30" rx="7" fill={toRgba(primary, 0.24)} />
        <rect x="234" y="44" width="64" height="26" rx="6" fill={toRgba(primary, 0.28)} />

        <path d="M70 92 C98 74, 128 74, 156 92" fill="none" stroke={toRgba(primary, 0.58)} strokeWidth="3" />
        <path d="M170 92 C200 74, 228 74, 258 92" fill="none" stroke={toRgba(primary, 0.58)} strokeWidth="3" />
        <path d="M260 92 C274 82, 288 82, 302 92" fill="none" stroke={toRgba(primary, 0.58)} strokeWidth="3" />

        <circle cx="286" cy="56" r="12" fill={toRgba(primary, 0.18)} stroke={toRgba(primary, 0.42)} strokeWidth="2" />
        <circle cx="286" cy="56" r="3" fill={primary}>
          <animate attributeName="r" values="2.5;4;2.5" dur="1s" repeatCount="indefinite" />
        </circle>

        <rect x="56" y="96" width="248" height="8" rx="4" fill={toRgba(text, 0.18)} />
        <rect x="56" y="96" width="0" height="8" rx="4" fill={primary}>
          <animate attributeName="width" values="0;248;0" dur="2.1s" repeatCount="indefinite" />
        </rect>
      </Box>
    );
  }

  if (variant === 'interior') {
    return (
      <Box
        component="svg"
        viewBox="0 0 360 140"
        role="img"
        aria-label="Guia animada para volante"
        sx={{ width: '100%', maxWidth: 360, height: 'auto', display: 'block', mx: 'auto', my: 1 }}
      >
        <rect x="20" y="18" width="320" height="104" rx="12" fill={panelFill} stroke={line} strokeWidth="2" />
        <circle cx="118" cy="72" r="34" fill="none" stroke={primary} strokeWidth="7" />
        <circle cx="118" cy="72" r="10" fill={secondary}>
          <animate attributeName="fill" values={`${secondary};${primary};${secondary}`} dur="1.6s" repeatCount="indefinite" />
        </circle>
        <rect x="182" y="48" width="126" height="12" rx="4" fill={toRgba(text, 0.25)} />
        <rect x="182" y="68" width="106" height="10" rx="4" fill={toRgba(text, 0.22)} />
        <rect x="182" y="84" width="88" height="10" rx="4" fill={toRgba(text, 0.22)} />
      </Box>
    );
  }

  if (variant === 'exterior') {
    return (
      <Box
        component="svg"
        viewBox="0 0 360 140"
        role="img"
        aria-label="Guia animada para captura exterior"
        sx={{ width: '100%', maxWidth: 360, height: 'auto', display: 'block', mx: 'auto', my: 1 }}
      >
        <rect x="18" y="18" width="324" height="104" rx="14" fill={panelFill} stroke={line} strokeWidth="2" />
        <rect x="52" y="54" width="256" height="38" rx="8" fill={toRgba(primary, 0.25)} stroke={toRgba(primary, 0.65)} strokeWidth="2" />
        <circle cx="100" cy="96" r="12" fill="#111827" />
        <circle cx="258" cy="96" r="12" fill="#111827" />
        <rect x="42" y="44" width="276" height="58" rx="10" fill="none" stroke={primary} strokeDasharray="8 6" strokeWidth="2">
          <animate attributeName="stroke-dashoffset" values="0;28" dur="1.6s" repeatCount="indefinite" />
        </rect>
      </Box>
    );
  }

  if (variant === 'document') {
    return (
      <Box
        component="svg"
        viewBox="0 0 360 140"
        role="img"
        aria-label="Guia animada para captura de documento"
        sx={{ width: '100%', maxWidth: 360, height: 'auto', display: 'block', mx: 'auto', my: 1 }}
      >
        <rect x="48" y="16" width="264" height="108" rx="10" fill="#fff" opacity="0.96" stroke={line} strokeWidth="2" />
        <rect x="66" y="38" width="92" height="64" rx="6" fill={panelFill} stroke={line} strokeWidth="1.2" />
        <rect x="172" y="42" width="118" height="8" rx="3" fill={toRgba(text, 0.25)} />
        <rect x="172" y="58" width="102" height="8" rx="3" fill={toRgba(text, 0.25)} />
        <rect x="172" y="74" width="86" height="8" rx="3" fill={toRgba(text, 0.25)} />
        <rect x="58" y="26" width="244" height="88" rx="8" fill="none" stroke={primary} strokeWidth="2" strokeDasharray="8 6">
          <animate attributeName="stroke-dashoffset" values="0;28" dur="1.4s" repeatCount="indefinite" />
        </rect>
        <circle cx="302" cy="34" r="5" fill={secondary}>
          <animate attributeName="fill" values={`${secondary};${primary};${secondary}`} dur="1s" repeatCount="indefinite" />
        </circle>
      </Box>
    );
  }

  return (
    <Box
      component="svg"
      viewBox="0 0 360 140"
      role="img"
      aria-label="Guia animada para panel de instrumentos"
      sx={{ width: '100%', maxWidth: 360, height: 'auto', display: 'block', mx: 'auto', my: 1 }}
    >
      <rect x="18" y="20" width="324" height="100" rx="14" fill={panelFill} stroke={line} strokeWidth="2" />
      <rect x="34" y="32" width="292" height="76" rx="14" fill="#0e1422" stroke={toRgba(primary, 0.55)} strokeWidth="2" />

      <circle cx="90" cy="70" r="26" fill="none" stroke={toRgba(primary, 0.36)} strokeWidth="4" />
      <circle cx="90" cy="70" r="3" fill={primary} />
      <line x1="90" y1="70" x2="107" y2="57" stroke={primary} strokeWidth="2.6">
        <animateTransform attributeName="transform" type="rotate" values="-16 90 70; 24 90 70; -16 90 70" dur="1.8s" repeatCount="indefinite" />
      </line>

      <circle cx="270" cy="70" r="26" fill="none" stroke={toRgba(primary, 0.36)} strokeWidth="4" />
      <circle cx="270" cy="70" r="3" fill={primary} />
      <line x1="270" y1="70" x2="285" y2="80" stroke={primary} strokeWidth="2.6">
        <animateTransform attributeName="transform" type="rotate" values="22 270 70; -14 270 70; 22 270 70" dur="1.8s" repeatCount="indefinite" />
      </line>

      <rect x="136" y="46" width="88" height="46" rx="8" fill="#111827" stroke={glow} strokeWidth="1.2" />
      <text x="181" y="73" textAnchor="middle" fontSize="16" fontFamily="monospace" fill="#E5E7EB">
        012345 km
      </text>
      <circle cx="128" cy="46" r="4" fill={secondary}>
        <animate attributeName="opacity" values="0.3;1;0.3" dur="1.2s" repeatCount="indefinite" />
      </circle>
      <rect x="56" y="100" width="248" height="6" rx="3" fill={toRgba(primary, 0.2)} />
      <rect x="56" y="100" width="0" height="6" rx="3" fill={primary}>
        <animate attributeName="width" values="0;248;0" dur="2.2s" repeatCount="indefinite" />
      </rect>
    </Box>
  );
};

export default GuidedHintAnimation;
